import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { Controller } from 'react-hook-form'
import { FormControl, InputLabel, TextField } from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'

const filter = createFilterOptions<SelectOption>()

interface Props {
  name: string
  label?: string
  control?: any
  options: SelectOption[]
  initialValue: SelectOption | null
  disabled?: boolean
  draggable?: boolean
  className?: string
  onChange?: (index: number, value: string | number | null) => void
}

export default function CreatableSelect({
  name,
  label,
  control,
  options,
  initialValue,
  disabled = false,
  draggable = false,
  className,
  onChange,
}: Props): JSX.Element {
  const intl = useIntl()
  const [value, setValue] = useState<SelectOption | null>(initialValue)

  useEffect(() => {
    if ((initialValue && !value) || initialValue !== value) {
      setValue(initialValue)
    }
  }, [initialValue, value])

  const handleChange = (newValue: SelectOption | string | null) => {
    if (typeof newValue === 'string') {
      newValue = { key: newValue, label: newValue }
    }
    if (newValue) {
      const value = { key: newValue?.key, label: newValue?.label?.replace('Add', '').replaceAll('"', '') }
      setValue(value)
      if (onChange) {
        const index = parseInt(name.split('-')[1])
        onChange(index, newValue?.key)
      }
      return value
    }
    if (onChange) {
      const index = parseInt(name.split('-')[1])
      onChange(index, null)
    }
    return null
  }

  const handleKeyDown = (event: any) => {
    if (event.key === 'Tab') {
      const value = event.target.value
      if (value.length > 0) {
        handleChange(value)
      }
    }
  }

  const getParams = (params: any) => {
    params.inputProps.onKeyDown = handleKeyDown
    return params
  }

  const getOptionLabel = (option: SelectOption | string): string => {
    if (typeof option === 'string') {
      return option
    }
    return option.label || ''
  }

  return (
    <>
      <FormControl margin="normal" className={className} draggable={draggable} disabled={disabled}>
        {label && <FieldLabel>{label}</FieldLabel>}
        {control ? (
          <Controller
            control={control}
            name={name}
            defaultValue={value}
            render={({ field: { onChange } }) => (
              <Autocomplete
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                freeSolo
                onChange={(event, newValue) => onChange(handleChange(newValue))}
                options={options}
                getOptionLabel={(option) => getOptionLabel(option)}
                autoHighlight
                value={value}
                renderInput={(params) => (
                  <StyledTextField
                    {...getParams(params)}
                    variant="outlined"
                    placeholder={intl.formatMessage({ id: 'strings.search' })}
                    disabled={disabled}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.key === value.key}
                disabled={disabled}
              />
            )}
          />
        ) : (
          <Autocomplete
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            freeSolo
            onChange={(event, newValue) => handleChange(newValue)}
            options={options}
            getOptionLabel={(option) => getOptionLabel(option)}
            autoHighlight
            value={value}
            renderInput={(params) => (
              <StyledTextField
                {...getParams(params)}
                variant="outlined"
                placeholder={intl.formatMessage({ id: 'strings.search' })}
              />
            )}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)

              if (params.inputValue !== '') {
                filtered.push({
                  key: params.inputValue,
                  label: `Add "${params.inputValue}"`,
                })
              }

              return filtered
            }}
          />
        )}
      </FormControl>
    </>
  )
}

const FieldLabel = styled(InputLabel)`
  padding: 0 10px;
  margin-top: -20px;
  transition: none;
  transform: none;
`

const StyledTextField = styled(TextField)`
  & input {
    width: 100% !important;
  }

  & .MuiAutocomplete-endAdornment {
    top: 0;
  }
`
